import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/billing";
import { PLAN_FEATURES } from "@/lib/plans";

export function DemoPlans({ pricing, pricingLoading, pricingError, billingCountry, onCountry, countries }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const accountHref = user ? "/dashboard" : "/register";
  const planPrice = (tier) => {
    if (tier === "free") return t("plan_free");
    const plan = pricing?.plans?.find((item) => item.tier === tier);
    if (!billingCountry) return t("billing_select_country");
    if (pricingLoading) return t("loading");
    if (pricingError || !pricing?.country || !plan?.available) return t("billing_unavailable");
    return `${formatMoney(plan.amount, plan.currency, lang)} ${t("per_month")}`;
  };
  return (
    <section className="mt-8" aria-labelledby="demo-plans-title">
      <div className="mb-4">
        <h2 id="demo-plans-title" className="font-serif text-2xl font-semibold">{t("demo_plan_compare_title")}</h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{t("demo_plan_compare_text")}</p>
        <div className="mt-4 rounded-2xl border border-border bg-white/70 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label htmlFor="demo-billing-country" className="text-sm font-semibold">{t("billing_country")}</label>
            <select id="demo-billing-country" value={billingCountry} onChange={(e) => onCountry(e.target.value)} className="h-10 rounded-full border border-border bg-card px-3 text-sm sm:min-w-56" data-testid="demo-billing-country">
              <option value="">{t("billing_select_country")}</option>
              {countries.map((c) => <option key={c} value={c}>{t(`cn_${c}`)}</option>)}
            </select>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t("billing_country_help")}</p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {["free", "plus", "premium"].map((tier) => (
          <article key={tier} data-testid={`demo-plan-${tier}`} className={`chenia-card flex flex-col p-5 ${tier === "plus" ? "border-primary ring-1 ring-primary/30" : ""}`}>
            <h3 className="font-serif text-xl font-semibold">{t(`plan_${tier}`)}</h3>
            <p className="mt-2 text-sm font-semibold text-chenia-terracottadark" data-testid={`demo-plan-price-${tier}`}>{planPrice(tier)}</p>
            {tier !== "free" && pricing?.country && pricing?.plans?.find((item) => item.tier === tier)?.fallback && <p className="mt-1 text-xs text-muted-foreground">{t("billing_fallback_chf")}</p>}
            <ul className="mt-4 flex-1 space-y-2">
              {PLAN_FEATURES[tier].map((f) => <li key={f} className="flex gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-chenia-sage" /><span>{t(f)}</span></li>)}
            </ul>
            {tier === "free"
              ? <Link to={accountHref}><Button variant="outline" className="mt-5 w-full rounded-full">{t("demo_choose_free")}</Button></Link>
              : <Link to={user ? `/pricing?plan=${tier}` : `/register?plan=${tier}`}><Button className="mt-5 w-full rounded-full">{t("demo_choose_plan", { plan: t(`plan_${tier}`) })}</Button></Link>}
          </article>
        ))}
      </div>
    </section>
  );
}
